/**
 * useStandaloneSectionShortcuts — ⌘1…⌘6 section switching for the installed
 * app (macOS Dock app / desktop PWA window), the convention native Mac apps
 * use (Mail, Things, Finder).
 *
 * Registered ONLY when running standalone: inside a browser tab ⌘1-9 must
 * keep their native meaning (tab switching — reserved by the browser
 * anyway). The modifier goes through isPrimaryModPressed, so it's ⌘ on
 * Apple and Ctrl in installed windows elsewhere.
 */

import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

import { isPrimaryModPressed } from '../lib/platform';
import { isStandalone } from '../lib/pwa';

/** Slots follow the sidebar order: CORE → GROWTH → SYSTEM. */
export const SECTION_SHORTCUT_PATHS: readonly string[] = [
  '/business', // ⌘1 Business Hub
  '/studio', // ⌘2 Automations
  '/chat', // ⌘3 Workspace
  '/marketing/social', // ⌘4 Social Automator
  '/inbox', // ⌘5 Unified Inbox
  '/account', // ⌘6 Account
];

export function useStandaloneSectionShortcuts(): void {
  const navigate = useNavigate();

  useEffect(() => {
    if (!isStandalone()) return;

    const onKey = (e: KeyboardEvent): void => {
      if (!isPrimaryModPressed(e) || e.altKey || e.shiftKey) return;
      const slot = Number.parseInt(e.key, 10);
      if (!Number.isInteger(slot) || slot < 1 || slot > SECTION_SHORTCUT_PATHS.length) return;
      e.preventDefault();
      navigate(SECTION_SHORTCUT_PATHS[slot - 1]);
    };

    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [navigate]);
}
